/**
 * Les formes que l'écran dessine, tirées de la réponse de l'API : le schéma du
 * flux (services → parties) et les barres des douze mois. Fonctions pures,
 * sans mise en page ni texte : le dessin et les libellés vivent dans les
 * composants.
 *
 * Aucun bénéficiaire n'est connu d'avance. La COULEUR d'une partie vient de son
 * rang dans le partage (l'ordre que l'API sert), jamais de son nom : on la
 * retrouve à l'identique dans le flux, les barres et la grille.
 */
import type { CSSProperties } from 'react';
import type {
  ComptabiliteAssocie,
  ComptabiliteDirection,
  MoisComptable,
  MoisComptableAssocie,
  TypeRecetteId,
} from '../../types.js';

/** Combien de couleurs de rang avant de les reprendre (`--rang-0` à `--rang-5`). */
export const NB_RANGS = 6;

/** Identifiant de la partie « non réparti » (direction) ou « reste du HT » (associé). */
export const RESTE = 'reste';
/** Identifiant de la part du lecteur dans la vue associé. */
export const MA_PART = 'moi';

/** La couleur d'une partie par son rang ; `null` : ce qui n'est pas réparti. */
export function styleRang(rang: number | null): CSSProperties {
  const couleur = rang === null ? 'var(--rang-reste)' : `var(--rang-${String(rang % NB_RANGS)})`;
  return { '--c': couleur } as CSSProperties;
}

/**
 * Les bénéficiaires dans l'ordre du partage : d'abord ceux que la période
 * mentionne, tels que l'API les sert, puis ceux que l'appelant ajoute (la liste
 * du partage, pour la grille). Le rang d'un bénéficiaire ne dépend donc jamais
 * de ce qui est chargé après la page.
 */
export function ordreBeneficiaires(
  donnees: ComptabiliteDirection,
  supplements: readonly string[] = [],
): string[] {
  const ordre: string[] = [];
  const ajouter = (id: string): void => {
    if (!ordre.includes(id)) ordre.push(id);
  };
  for (const ligne of donnees.totaux.parBeneficiaire) ajouter(ligne.beneficiaireId);
  for (const mois of donnees.mois) {
    for (const ligne of mois.parBeneficiaire) ajouter(ligne.beneficiaireId);
  }
  for (const id of supplements) ajouter(id);
  return ordre;
}

/** Les libellés des bénéficiaires que la réponse nomme. */
export function libellesDe(donnees: ComptabiliteDirection): Map<string, string> {
  const libelles = new Map<string, string>();
  for (const ligne of donnees.totaux.parBeneficiaire) {
    libelles.set(ligne.beneficiaireId, ligne.libelle);
  }
  for (const mois of donnees.mois) {
    for (const ligne of mois.parBeneficiaire) libelles.set(ligne.beneficiaireId, ligne.libelle);
  }
  return libelles;
}

// ── Le flux : du HT des services vers les parties ───────────────────────

export interface NoeudService {
  service: TypeRecetteId;
  htCentimes: number;
}

export interface NoeudPartie {
  id: string;
  /** `null` pour la partie « reste » : pas de rang, pas de bénéficiaire. */
  libelle: string | null;
  rang: number | null;
  montantCentimes: number;
}

export interface LienFlux {
  service: TypeRecetteId;
  partie: string;
  montantCentimes: number;
  /** La part du HT du service que ce lien porte, de 0 à 1. */
  partDuService: number;
}

export interface SchemaFlux {
  services: NoeudService[];
  parties: NoeudPartie[];
  liens: LienFlux[];
  totalCentimes: number;
}

/** Du HT de chaque service vers chaque bénéficiaire, plus ce qui n'est pas réparti. */
export function fluxDirection(mois: MoisComptable, ordre: readonly string[]): SchemaFlux {
  const services = mois.services.filter((s) => s.htCentimes > 0);
  const liens: LienFlux[] = [];
  const totaux = new Map<string, NoeudPartie>();
  let nonReparti = 0;
  for (const service of services) {
    if (service.regle === null || service.parts.length === 0) {
      nonReparti += service.htCentimes;
      liens.push({
        service: service.service,
        partie: RESTE,
        montantCentimes: service.htCentimes,
        partDuService: 1,
      });
      continue;
    }
    for (const part of service.parts) {
      if (part.montantCentimes <= 0) continue;
      const rang = ordre.indexOf(part.beneficiaireId);
      const noeud = totaux.get(part.beneficiaireId) ?? {
        id: part.beneficiaireId,
        libelle: part.libelle,
        rang: rang === -1 ? ordre.length : rang,
        montantCentimes: 0,
      };
      noeud.montantCentimes += part.montantCentimes;
      totaux.set(part.beneficiaireId, noeud);
      liens.push({
        service: service.service,
        partie: part.beneficiaireId,
        montantCentimes: part.montantCentimes,
        partDuService: part.montantCentimes / service.htCentimes,
      });
    }
  }
  const parties = [...totaux.values()].sort((a, b) => (a.rang ?? 0) - (b.rang ?? 0));
  if (nonReparti > 0) {
    parties.push({ id: RESTE, libelle: null, rang: null, montantCentimes: nonReparti });
  }
  return {
    services: services.map((s) => ({ service: s.service, htCentimes: s.htCentimes })),
    parties,
    liens,
    totalCentimes: services.reduce((total, s) => total + s.htCentimes, 0),
  };
}

/** Du HT de chaque service vers « votre part » et « le reste du HT ». */
export function fluxAssocie(mois: MoisComptableAssocie): SchemaFlux {
  const services = mois.services.filter((s) => s.htCentimes > 0);
  const liens: LienFlux[] = [];
  let maPart = 0;
  let reste = 0;
  for (const service of services) {
    const mienne = service.mesParts.reduce((total, p) => total + p.montantCentimes, 0);
    const autre = service.htCentimes - mienne;
    if (mienne > 0) {
      maPart += mienne;
      liens.push({
        service: service.service,
        partie: MA_PART,
        montantCentimes: mienne,
        partDuService: mienne / service.htCentimes,
      });
    }
    if (autre > 0) {
      reste += autre;
      liens.push({
        service: service.service,
        partie: RESTE,
        montantCentimes: autre,
        partDuService: autre / service.htCentimes,
      });
    }
  }
  const parties: NoeudPartie[] = [];
  if (maPart > 0) {
    parties.push({ id: MA_PART, libelle: null, rang: 0, montantCentimes: maPart });
  }
  if (reste > 0) parties.push({ id: RESTE, libelle: null, rang: null, montantCentimes: reste });
  return {
    services: services.map((s) => ({ service: s.service, htCentimes: s.htCentimes })),
    parties,
    liens,
    totalCentimes: services.reduce((total, s) => total + s.htCentimes, 0),
  };
}

/** `AAAA-MM` décalé de `n` mois (négatif : vers le passé). */
export function moisPlus(mois: string, n: number): string {
  const [annee, numero] = mois.split('-').map(Number) as [number, number];
  const date = new Date(Date.UTC(annee, numero - 1 + n, 1));
  return `${String(date.getUTCFullYear())}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ── Les douze mois ──────────────────────────────────────────────────────

export interface SegmentBarre {
  id: string;
  libelle: string | null;
  rang: number | null;
  montantCentimes: number;
}

export interface BarreMois {
  mois: string;
  provisoire: boolean;
  htCentimes: number;
  /** Du bas vers le haut : les parties dans l'ordre du partage, puis le non réparti. */
  segments: SegmentBarre[];
}

export function barresDirection(
  donnees: ComptabiliteDirection,
  ordre: readonly string[],
): BarreMois[] {
  return donnees.mois.map((mois) => {
    const segments: SegmentBarre[] = mois.parBeneficiaire
      .filter((ligne) => ligne.montantCentimes > 0)
      .map((ligne) => {
        const rang = ordre.indexOf(ligne.beneficiaireId);
        return {
          id: ligne.beneficiaireId,
          libelle: ligne.libelle,
          rang: rang === -1 ? ordre.length : rang,
          montantCentimes: ligne.montantCentimes,
        };
      });
    const nonReparti = mois.services
      .filter((s) => s.regle === null)
      .reduce((total, s) => total + s.htCentimes, 0);
    if (nonReparti > 0) {
      segments.push({ id: RESTE, libelle: null, rang: null, montantCentimes: nonReparti });
    }
    return {
      mois: mois.mois,
      provisoire: mois.statut !== 'cloture',
      htCentimes: mois.totaux.htCentimes,
      segments,
    };
  });
}

/** Pour l'associé : la barre du HT du mois, et par-dessus sa part. */
export function barresAssocie(donnees: ComptabiliteAssocie): BarreMois[] {
  return donnees.mois.map((mois) => ({
    mois: mois.mois,
    provisoire: mois.statut !== 'cloture',
    htCentimes: mois.totalHtCentimes,
    segments:
      mois.monTotalCentimes > 0
        ? [{ id: MA_PART, libelle: null, rang: 0, montantCentimes: mois.monTotalCentimes }]
        : [],
  }));
}
